import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, GraduationCap, MousePointer2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1545044846-351ba102b6d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxNZWxib3VybmUlMjBza3lsaW5lJTIwQXVzdHJhbGlhJTIwdW5pdmVyc2l0eSUyMHN0dWRlbnRzJTIwZWR1Y2F0aW9ufGVufDB8fHx8MTc4ODQzOTAzMXww&ixlib=rb-4.1.0&q=85";

const LINES = [
  <>Your Trusted Pathway to</>,
  <>Study, Visa &amp; Migration</>,
  <>
    Success in <span className="bg-gradient-to-r from-sky-600 to-sky-400 bg-clip-text text-transparent">Australia</span>
  </>,
];

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-center overflow-hidden bg-gradient-to-b from-sky-100 via-sky-50 to-white pt-28 pb-20">
      <motion.div style={{ y: imgY }} className="pointer-events-none absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Melbourne skyline at dusk"
          className="h-full w-full object-cover opacity-50 [mask-image:linear-gradient(to_bottom,transparent_0%,rgba(0,0,0,0.6)_45%,black_100%)]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sky-100/70 via-sky-50/30 to-white" />
      </motion.div>

      <div className="pointer-events-none absolute -top-24 right-[-10%] h-[420px] w-[420px] rounded-full bg-sky-300/30 blur-3xl" />
      <div className="pointer-events-none absolute bottom-[-15%] left-[-8%] h-[380px] w-[380px] rounded-full bg-sky-200/40 blur-3xl" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/70 px-4 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-sky-700 backdrop-blur"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
          Education &amp; Migration Consultancy — Melbourne
        </motion.p>

        <h1 className="max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] text-slate-900 sm:text-5xl lg:text-6xl">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7, ease: EASE }}
          className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
        >
          We guide students and migrants through admissions, visas, and permanent
          residency with expert, personalised support — from your first enquiry to
          your new life in Australia.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-free-consultation"
            onClick={() => scrollToId("contact")}
            className="group flex items-center gap-2.5 rounded-full bg-sky-600 px-7 py-4 text-sm font-semibold text-white shadow-[0_12px_36px_rgba(2,132,199,0.4)] transition-all duration-300 hover:-translate-y-1 hover:bg-sky-500 hover:shadow-[0_18px_44px_rgba(2,132,199,0.5)]"
          >
            Get a Consultation
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-explore-services"
            onClick={() => scrollToId("services")}
            className="rounded-full border border-sky-300 bg-white/70 px-7 py-4 text-sm font-semibold text-sky-700 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-sky-400 hover:bg-white"
          >
            Explore Our Services
          </button>
        </motion.div>
      </div>

      <motion.div
        style={{ y: cardY }}
        className="pointer-events-none absolute right-[6%] top-[22%] z-10 hidden lg:block"
      >
        <motion.div
          animate={{ y: [0, -14, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="rounded-2xl border border-sky-200/70 bg-white/80 p-5 shadow-[0_20px_60px_rgba(2,132,199,0.18)] backdrop-blur-xl"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-600 text-white">
              <GraduationCap className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-sm font-bold text-slate-900">Admissions to PR</p>
              <p className="text-xs text-slate-500">One guided pathway, end to end</p>
            </div>
          </div>
        </motion.div>
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="ml-16 mt-6 rounded-2xl border border-sky-200/70 bg-white/80 p-5 shadow-[0_20px_60px_rgba(2,132,199,0.18)] backdrop-blur-xl"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-sky-600 ring-1 ring-sky-200">
              <BadgeCheck className="h-5 w-5" />
            </span>
            <div>
              <p className="font-display text-sm font-bold text-slate-900">500 · 485 · 189 · 190 · 491</p>
              <p className="text-xs text-slate-500">Every major visa subclass covered</p>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-sky-700/70 sm:flex"
      >
        <MousePointer2 className="h-3.5 w-3.5 animate-bounce" />
        Scroll to explore
      </motion.button>
    </section>
  );
}
